/**
 * Settings → Branding (PHP-3378): a business's logo and brand colour, each kept separate per
 * organization, versioned and audited. The chosen colour is checked against WCAG AA (4.5:1 against a
 * white background) both locally (as the owner types, before they submit) and again by the server.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, FullPageSpinner, Section, buttonClass, cx, useToast } from "@/components/ui";
import { checkContrast } from "@/lib/contrast";
import { portal } from "@/lib/api";
import { ApiError } from "@/lib/http";
import type { BusinessBranding } from "@/lib/types";

import { ReadOnlyNote } from "./parts";

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const DEFAULT_COLOUR = "#1a73e8";

export function BrandingTab() {
  const { orgId } = useOrganization();
  const branding = useQuery({ queryKey: ["branding", orgId], queryFn: () => portal.branding(orgId) });
  if (branding.isPending) return <FullPageSpinner />;
  if (branding.isError) return <ErrorAlert error={branding.error} />;
  return <BrandingForm key={orgId} saved={branding.data} />;
}

function BrandingForm({ saved }: { saved: BusinessBranding }) {
  const { orgId, can } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const editable = can("organization:update");
  const [colour, setColour] = useState(saved.colour_hex ?? DEFAULT_COLOUR);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFileError, setLogoFileError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const existingLogo = useQuery({
    queryKey: ["branding-logo", orgId, saved.logo?.content_type, saved.version],
    queryFn: () => portal.brandingLogo(orgId),
    enabled: Boolean(saved.logo) && !logoPreview,
  });
  const existingLogoBlob = existingLogo.data instanceof Blob ? existingLogo.data : null;
  // Creating the object URL here (not in an effect) keeps it in sync with the blob in the same render;
  // the effect below only ever revokes it, it never derives state from it.
  const existingLogoUrl = useMemo(
    () => (existingLogoBlob ? URL.createObjectURL(existingLogoBlob) : null),
    [existingLogoBlob],
  );
  useEffect(() => {
    return () => {
      if (existingLogoUrl) URL.revokeObjectURL(existingLogoUrl);
    };
  }, [existingLogoUrl]);
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const saveColour = useMutation({
    mutationFn: (colour_hex: string) => portal.saveBrandingColour(orgId, colour_hex),
    onSuccess: (b) => {
      qc.setQueryData(["branding", orgId], b);
      toast.show({ tone: "success", title: "Brand colour saved" });
    },
  });
  const uploadLogo = useMutation({
    mutationFn: (file: File) => portal.uploadBrandingLogo(orgId, file),
    onSuccess: (b) => {
      qc.setQueryData(["branding", orgId], b);
      void qc.invalidateQueries({ queryKey: ["branding-logo", orgId] });
      toast.show({ tone: "success", title: "Logo saved" });
    },
  });

  const latest = saveColour.data ?? saved;
  const live = checkContrast(colour);
  const colourError =
    saveColour.error instanceof ApiError
      ? (saveColour.error.fieldError("colour_hex") ?? saveColour.error.message)
      : null;
  const unsavedColour = colour.toLowerCase() !== (latest.colour_hex ?? "").toLowerCase();

  const commitColour = () => {
    if (!editable || !live || !unsavedColour) return;
    saveColour.mutate(colour);
  };

  const pickLogo = (file: File | null) => {
    setLogoFileError(null);
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setLogoFileError("Upload a PNG, JPEG, WEBP or SVG image");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoFileError(`Keep your logo under ${MAX_LOGO_BYTES / (1024 * 1024)} MB`);
      return;
    }
    setLogoPreview(URL.createObjectURL(file));
    uploadLogo.mutate(file, {
      onError: () => setLogoPreview(null),
    });
  };

  const logoError =
    uploadLogo.error instanceof ApiError
      ? (uploadLogo.error.fieldError("file") ?? uploadLogo.error.message)
      : null;
  const shownLogoUrl = logoPreview ?? existingLogoUrl;

  return (
    <div className="flex flex-col items-start gap-4 lg:flex-row">
      <div className="flex w-full min-w-0 flex-1 flex-col gap-4">
        <Section variant="tight">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-bold text-fg">Brand colour</h2>
            <p className="text-[12.5px] leading-[1.55] text-fg-subtle">
              Used across your customer-facing pages. Checked for accessibility (WCAG AA, 4.5:1) against a
              white background.
            </p>
          </div>
          <ReadOnlyNote permission="organization:update" />

          <div className="flex items-center gap-2.5">
            <input
              type="color"
              aria-label="Brand colour"
              value={/^#[0-9a-fA-F]{6}$/.test(colour) ? colour : DEFAULT_COLOUR}
              disabled={!editable}
              onChange={(e) => setColour(e.target.value)}
              onBlur={commitColour}
              className="size-[41px] shrink-0 cursor-pointer rounded-xl border border-border bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-60"
            />
            <input
              id="brand-colour-hex"
              aria-label="Brand colour hex value"
              value={colour}
              disabled={!editable}
              onChange={(e) => setColour(e.target.value)}
              onBlur={commitColour}
              placeholder="#1a73e8"
              maxLength={7}
              className={cx(
                "h-[41px] w-32 rounded-xl bg-surface px-3.5 font-mono text-[13.5px] text-fg placeholder:text-fg-subtle focus:outline-none disabled:opacity-60",
                colourError
                  ? "border-[1.5px] border-red-500"
                  : "border-[1.5px] border-border focus:border-brand-500",
              )}
            />
            {saveColour.isPending && <span className="text-xs text-fg-subtle">Saving…</span>}
          </div>

          {live && (
            <div
              className={cx(
                "flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs leading-[1.5]",
                live.passes_aa ? "bg-[#e8f6ee] text-[#13734d]" : "bg-[#fdeee9] text-[#9a3412]",
              )}
            >
              {live.passes_aa ? (
                <CircleCheck aria-hidden className="size-[13px] shrink-0" />
              ) : (
                <CircleAlert aria-hidden className="size-[13px] shrink-0" />
              )}
              {live.passes_aa
                ? `Contrast against white is ${live.ratio_white}:1 — reads clearly (WCAG AA).`
                : `Contrast against white is only ${live.ratio_white}:1 — WCAG AA needs at least 4.5:1. Choose a darker or more saturated shade.`}
            </div>
          )}
          {!live && colour && <p className="text-xs text-red-600">Use a 6-digit hex colour, like #1A73E8.</p>}
          {colourError && <p className="text-xs text-red-600">{colourError}</p>}
        </Section>

        <Section variant="tight">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-base font-bold text-fg">Logo</h2>
            <p className="text-[12.5px] leading-[1.55] text-fg-subtle">
              PNG, JPEG, WEBP or SVG, up to 2 MB. Previewed here before it's uploaded.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-brand-50">
              {shownLogoUrl ? (
                <img src={shownLogoUrl} alt="Your business logo" className="size-full object-contain" />
              ) : (
                <span className="text-[11px] text-fg-subtle">No logo</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={fileInput}
                type="file"
                accept={ALLOWED_LOGO_TYPES.join(",")}
                disabled={!editable}
                onChange={(e) => pickLogo(e.target.files?.[0] ?? null)}
                className="hidden"
              />
              <Button
                unstyled
                requires="organization:update"
                loading={uploadLogo.isPending}
                onClick={() => fileInput.current?.click()}
                className={buttonClass("secondary", "pill")}
              >
                {uploadLogo.isPending ? "Uploading…" : saved.logo ? "Replace logo" : "Upload logo"}
              </Button>
              {saved.logo && (
                <p className="text-xs text-fg-subtle">
                  {saved.logo.width && saved.logo.height
                    ? `${saved.logo.width} × ${saved.logo.height} px · `
                    : ""}
                  {(saved.logo.size_bytes / 1024).toFixed(0)} KB
                </p>
              )}
            </div>
          </div>
          {logoFileError && <p className="text-xs text-red-600">{logoFileError}</p>}
          {logoError && <p className="text-xs text-red-600">{logoError}</p>}
        </Section>
      </div>

      <div className="flex w-full shrink-0 flex-col gap-4 lg:w-[360px]">
        <Section variant="tight">
          <h2 className="text-[15px] font-bold text-fg">Preview</h2>
          <div
            className="flex flex-col items-center gap-3 rounded-[18px] p-5"
            style={{ backgroundColor: live?.colour_hex ?? "#1a73e8" }}
          >
            {shownLogoUrl ? (
              <img src={shownLogoUrl} alt="" className="h-10 max-w-full object-contain" />
            ) : (
              <span className="text-sm font-semibold text-white">Your business</span>
            )}
          </div>
          <p className="text-xs leading-[1.5] text-fg-subtle">
            A quick look at your logo on your brand colour. Changes save as you go — version {latest.version}.
          </p>
        </Section>
      </div>
    </div>
  );
}
